import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
  Grid,
  Paper,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  Checkbox,
} from '@mui/material'
import {
  ChevronLeft,
  ChevronRight,
  Today,
  Search,
  Refresh,
  CheckCircle,
  Cancel,
  Schedule,
  People,
  Edit,
  Save,
  Close,
} from '@mui/icons-material'
import { attendanceAPI } from '../../services/api'

interface DailyOverview {
  date: string
  total_employees: number
  present_count: number
  absent_count: number
  half_day_count: number
  total_daily_cost: number
  employees: Array<{
    id: number
    employee_id: number
    employee_id_number: string
    employee_name: string
    first_name?: string
    last_name?: string
    full_name?: string
    department?: string
    position?: string
    is_active?: boolean
    attendance_id?: number
    attendance_date: string
    status: string
    hours_worked: number
    check_in_time?: string
    check_out_time?: string
    notes?: string
    created_at?: string
    updated_at?: string
    compensation_structure_type?: string
    effective_hourly_rate?: number
    daily_earnings_estimate?: number
    monthly_salary?: number
  }>
}

const AttendanceManagementPageFinal: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0])
  const [overview, setOverview] = useState<DailyOverview | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedEmployees, setSelectedEmployees] = useState<Set<number>>(new Set())
  const [editingEmployee, setEditingEmployee] = useState<number | null>(null)
  const [editedHours, setEditedHours] = useState<string>('')
  const [editedNotes, setEditedNotes] = useState<string>('')
  const [updatingEmployee, setUpdatingEmployee] = useState<number | null>(null)

  useEffect(() => {
    loadDailyOverview()
  }, [selectedDate])

  const loadDailyOverview = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await attendanceAPI.getDailyOverview(selectedDate)
      console.log('[DEBUG] API Response:', data)
      console.log('[DEBUG] Employees:', data?.employees)
      console.log('[DEBUG] First employee:', data?.employees?.[0])
      setOverview(data)
    } catch (err: any) {
      console.error('[DEBUG] Error loading overview:', err)
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to load attendance data'
      setError(errorMsg)
    } finally {
      setLoading(false)
    }
  }

  const handlePreviousDay = () => {
    const date = new Date(selectedDate)
    date.setDate(date.getDate() - 1)
    setSelectedDate(date.toISOString().split('T')[0])
  }

  const handleNextDay = () => {
    const date = new Date(selectedDate)
    date.setDate(date.getDate() + 1)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    
    // Prevent selecting future dates
    if (date <= today) {
      setSelectedDate(date.toISOString().split('T')[0])
    }
  }

  const handleToday = () => {
    setSelectedDate(new Date().toISOString().split('T')[0])
  }

  const handleStatusChange = async (employeeId: number, newStatus: 'present' | 'absent' | 'half_day') => {
    setUpdatingEmployee(employeeId)
    setError(null)
    setSuccessMessage(null)

    try {
      const defaultHours = newStatus === 'present' ? 7.0 : newStatus === 'half_day' ? 3.5 : 0
      
      await attendanceAPI.bulkUpdate([{
        employee_id: employeeId,
        attendance_date: selectedDate,
        status: newStatus,
        hours_worked: defaultHours,
      }])
      
      const statusLabel = newStatus === 'present' ? 'Present' : newStatus === 'half_day' ? 'Half-Day' : 'Absent'
      setSuccessMessage(`Status updated to ${statusLabel}`)
      
      await loadDailyOverview()
      
      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update attendance')
    } finally {
      setUpdatingEmployee(null)
    }
  }

  const handleEditHours = (employeeId: number, currentHours: number, currentNotes: string) => {
    setEditingEmployee(employeeId)
    setEditedHours(currentHours.toString())
    setEditedNotes(currentNotes || '')
  }

  const handleSaveEdit = async (employeeId: number, status: string) => {
    const hours = parseFloat(editedHours)
    if (isNaN(hours) || hours < 0 || hours > 12) {
      alert('Please enter valid hours (0-12)')
      return
    }

    try {
      await attendanceAPI.bulkUpdate([{
        employee_id: employeeId,
        attendance_date: selectedDate,
        status,
        hours_worked: hours,
        notes: editedNotes || null,
      }])
      setEditingEmployee(null)
      await loadDailyOverview()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update hours')
    }
  }

  const handleCancelEdit = () => {
    setEditingEmployee(null)
    setEditedHours('')
    setEditedNotes('')
  }

  const handleSelectEmployee = (employeeId: number, selected: boolean) => {
    setSelectedEmployees(prev => {
      const newSet = new Set(prev)
      if (selected) {
        newSet.add(employeeId)
      } else {
        newSet.delete(employeeId)
      }
      return newSet
    })
  }

  const handleSelectAll = () => {
    if (overview) {
      setSelectedEmployees(new Set(filteredRecords.map(emp => emp.employee_id)))
    }
  }

  const handleDeselectAll = () => {
    setSelectedEmployees(new Set())
  }

  const handleBulkAction = async (action: 'absent' | 'half_day') => {
    if (selectedEmployees.size === 0) {
      setError('Please select employees first')
      return
    }

    try {
      const updates = Array.from(selectedEmployees).map(employeeId => ({
        employee_id: employeeId,
        attendance_date: selectedDate,
        status: action,
        hours_worked: action === 'half_day' ? 3.5 : 0,
      }))

      await attendanceAPI.bulkUpdate(updates)
      setSelectedEmployees(new Set())
      await loadDailyOverview()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to perform bulk action')
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const filteredRecords = React.useMemo(() => {
    if (!overview?.employees || !Array.isArray(overview.employees)) return []
    if (!searchQuery || searchQuery.trim() === '') return overview.employees
    
    try {
      const query = String(searchQuery || '').toLowerCase()
      return overview.employees.filter(emp => {
        if (!emp) return false
        
        // Safely extract and convert values to strings, handling undefined/null
        const firstName = emp.first_name ? String(emp.first_name) : ''
        const lastName = emp.last_name ? String(emp.last_name) : ''
        const employeeName = emp.employee_name ? String(emp.employee_name) : ''
        const fullName = emp.full_name ? String(emp.full_name) : ''
        const combinedName = `${firstName} ${lastName}`.trim()
        
        const nameToSearch = (employeeName || fullName || combinedName || '').toLowerCase()
        const empIdNumber = emp.employee_id_number ? String(emp.employee_id_number).toLowerCase() : ''
        const dept = emp.department ? String(emp.department).toLowerCase() : ''
        
        return nameToSearch.includes(query) ||
          empIdNumber.includes(query) ||
          dept.includes(query)
      })
    } catch (error) {
      console.error('[DEBUG] Error filtering records:', error, 'Search query:', searchQuery, 'Overview:', overview)
      return overview.employees || []
    }
  }, [overview?.employees, searchQuery])

  const isWeekend = (dateStr: string) => {
    const day = new Date(dateStr).getDay()
    return day === 0 || day === 6
  }

  return (
    <Box sx={{
      p: { xs: 2, sm: 3 },
      pb: { xs: 10, sm: 3 },
      maxWidth: '100%',
      width: '100%',
      overflow: 'hidden',
      boxSizing: 'border-box',
      bgcolor: '#f5f7fa',
      minHeight: '100vh',
    }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" component="h1" fontWeight="bold" sx={{ color: 'text.primary' }}>
          Attendance Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Refresh />}
          onClick={loadDailyOverview}
          disabled={loading}
          sx={{
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 600,
            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
            minWidth: 'auto',
          }}
        >
          Refresh
        </Button>
      </Box>

      {/* Date Navigation */}
      <Card sx={{
        mb: 3,
        width: '100%',
        maxWidth: '100%',
        borderRadius: '20px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
      }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton onClick={handlePreviousDay} disabled={loading}>
                <ChevronLeft />
              </IconButton>
              <TextField
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                size="small"
                sx={{ minWidth: 200 }}
                inputProps={{
                  max: new Date().toISOString().split('T')[0], // Prevent future dates
                }}
              />
              <IconButton
                onClick={handleNextDay}
                disabled={loading || selectedDate >= new Date().toISOString().split('T')[0]}
              >
                <ChevronRight />
              </IconButton>
              <Button
                variant="outlined"
                startIcon={<Today />}
                onClick={handleToday}
                disabled={loading}
              >
                Today
              </Button>
              {isWeekend(selectedDate) && (
                <Chip label="Weekend" color="warning" size="small" />
              )}
            </Box>

            {/* Quick Stats */}
            {overview && (
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Chip
                  icon={<CheckCircle />}
                  label={`Present: ${overview.present_count}`}
                  color="success"
                  variant="outlined"
                />
                <Chip
                  icon={<Cancel />}
                  label={`Absent: ${overview.absent_count}`}
                  color="error"
                  variant="outlined"
                />
                <Chip
                  icon={<Schedule />}
                  label={`Half-Day: ${overview.half_day_count}`}
                  color="info"
                  variant="outlined"
                />
                <Chip
                  icon={<People />}
                  label={`Total: ${overview.total_employees}`}
                  variant="outlined"
                />
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Search and Bulk Actions */}
      <Card sx={{
        mb: 3,
        width: '100%',
        maxWidth: '100%',
        borderRadius: '20px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
      }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search employees..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{
                flexGrow: 1,
                minWidth: 250,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '16px',
                  bgcolor: 'white',
                  '& fieldset': {
                    borderColor: '#e0e0e0',
                  },
                },
              }}
            />
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={handleSelectAll}
                disabled={!overview || filteredRecords.length === 0}
              >
                Select All
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={handleDeselectAll}
                disabled={selectedEmployees.size === 0}
              >
                Deselect All
              </Button>
            </Box>

            {selectedEmployees.size > 0 && (
              <>
                <Divider orientation="vertical" flexItem />
                <Typography variant="body2" color="text.secondary">
                  {selectedEmployees.size} selected
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  color="error"
                  onClick={() => handleBulkAction('absent')}
                  sx={{
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  Mark Absent
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="info"
                  onClick={() => handleBulkAction('half_day')}
                  sx={{
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  Mark Half-Day
                </Button>
              </>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Employee List */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={{ xs: 2, sm: 2 }} sx={{
          width: '100%',
          maxWidth: '100%',
          m: 0,
          '& .MuiGrid-item': {
            paddingLeft: { xs: '8px', sm: '16px' },
            paddingTop: { xs: '8px', sm: '16px' },
          }
        }}>
          <Grid item xs={12} lg={9}>
            <Card sx={{
              width: '100%',
              maxWidth: '100%',
              borderRadius: '20px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
            }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Employees ({filteredRecords.length})
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                {filteredRecords.length === 0 ? (
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                      No employees found
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {filteredRecords.map((emp) => {
                      console.log('[DEBUG] Rendering employee:', emp.employee_id, emp)
                      return (
                      <Paper
                        key={`emp-${emp.employee_id}-${emp.id}`}
                        elevation={0}
                        sx={{
                          p: 2,
                          border: selectedEmployees.has(emp.employee_id) ? 2 : 1,
                          borderColor: selectedEmployees.has(emp.employee_id) ? 'primary.main' : '#e0e0e0',
                          borderRadius: '16px',
                          width: '100%',
                          maxWidth: '100%',
                          overflow: 'hidden',
                          boxSizing: 'border-box',
                          bgcolor: selectedEmployees.has(emp.employee_id) ? '#f0f4ff' : 'white',
                          transition: 'all 0.3s',
                          '&:hover': {
                            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                            transform: 'translateY(-2px)',
                          },
                        }}
                      >
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          flexWrap: { xs: 'wrap', md: 'nowrap' },
                        }}>
                          {/* Checkbox */}
                          <Checkbox
                            checked={selectedEmployees.has(emp.employee_id)}
                            onChange={(e) => handleSelectEmployee(emp.employee_id, e.target.checked)}
                            size="small"
                          />

                          {/* Employee Info */}
                          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                              <Typography variant="body1" fontWeight="medium" noWrap>
                                {emp.employee_name || emp.full_name || `${emp.first_name} ${emp.last_name}`}
                              </Typography>
                              <Chip label={emp.employee_id_number} size="small" variant="outlined" />
                              <Chip
                                key={`status-${emp.employee_id}-${emp.status}`}
                                label={
                                  emp.status === 'present' ? 'Present' :
                                  emp.status === 'half_day' ? 'Half-Day' :
                                  emp.status === 'absent' ? 'Absent' :
                                  `Status: ${emp.status}`
                                }
                                size="small"
                                color={
                                  emp.status === 'present' ? 'success' :
                                  emp.status === 'half_day' ? 'info' :
                                  emp.status === 'absent' ? 'error' :
                                  'default'
                                }
                                variant="filled"
                                sx={{
                                  fontWeight: 'bold',
                                  ...(emp.status === 'absent' && {
                                    backgroundColor: 'error.main',
                                    color: 'error.contrastText'
                                  }),
                                  ...(emp.status === 'half_day' && {
                                    backgroundColor: 'info.main',
                                    color: 'info.contrastText'
                                  }),
                                  ...(emp.status === 'present' && {
                                    backgroundColor: 'success.main',
                                    color: 'success.contrastText'
                                  })
                                }}
                              />
                            </Box>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {emp.department || 'No Department'} • {emp.position || 'No Position'}
                            </Typography>
                          </Box>

                          {/* Status Toggle */}
                          <ToggleButtonGroup
                            value={emp.status}
                            exclusive
                            onChange={(e, newStatus) => newStatus && handleStatusChange(emp.employee_id, newStatus)}
                            size="small"
                            disabled={editingEmployee === emp.employee_id || updatingEmployee === emp.employee_id}
                          >
                            <ToggleButton value="present" color="success">
                              <Tooltip title="Mark as Present (7 hours)">
                                <CheckCircle fontSize="small" />
                              </Tooltip>
                            </ToggleButton>
                            <ToggleButton value="half_day" color="info">
                              <Tooltip title="Mark as Half-Day (3.5 hours)">
                                <Schedule fontSize="small" />
                              </Tooltip>
                            </ToggleButton>
                            <ToggleButton value="absent" color="error">
                              <Tooltip title="Mark as Absent (0 hours)">
                                <Cancel fontSize="small" />
                              </Tooltip>
                            </ToggleButton>
                          </ToggleButtonGroup>

                          {/* Hours Display/Edit */}
                          {editingEmployee === emp.employee_id ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <TextField
                                size="small"
                                type="number"
                                value={editedHours}
                                onChange={(e) => setEditedHours(e.target.value)}
                                inputProps={{ min: 0, max: 12, step: 0.5 }}
                                sx={{ width: 80 }}
                                label="Hours"
                              />
                              <IconButton size="small" color="primary" onClick={() => handleSaveEdit(emp.employee_id, emp.status)}>
                                <Save fontSize="small" />
                              </IconButton>
                              <IconButton size="small" onClick={handleCancelEdit}>
                                <Close fontSize="small" />
                              </IconButton>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip
                                label={`${emp.hours_worked}h`}
                                size="small"
                                color={emp.status === 'present' ? 'success' : emp.status === 'half_day' ? 'info' : 'default'}
                              />
                              <IconButton size="small" onClick={() => handleEditHours(emp.employee_id, emp.hours_worked, emp.notes || '')}>
                                <Edit fontSize="small" />
                              </IconButton>
                            </Box>
                          )}

                          {/* Earnings */}
                          <Box sx={{ minWidth: 120, textAlign: 'right' }}>
                            <Typography variant="body2" fontWeight="medium" color="primary.main">
                              {formatCurrency(emp.daily_earnings_estimate || 0)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              @ {emp.effective_hourly_rate ? formatCurrency(emp.effective_hourly_rate) : 'N/A'}/hr
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                      )
                    })}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Summary Panel */}
          <Grid item xs={12} lg={3}>
            <Card sx={{
              width: '100%',
              maxWidth: '100%',
              borderRadius: '20px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -50,
                right: -50,
                width: 150,
                height: 150,
                borderRadius: '50%',
                bgcolor: 'rgba(255, 255, 255, 0.1)',
              },
            }}>
              <CardContent sx={{ position: 'relative', zIndex: 1 }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'white', fontWeight: 700, mb: 3 }}>
                  Daily Summary
                </Typography>
                
                {overview && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Date Card */}
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: 'rgba(255, 255, 255, 0.15)',
                        borderRadius: '16px',
                        backdropFilter: 'blur(10px)',
                      }}
                    >
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', textTransform: 'uppercase', letterSpacing: 1 }}>
                        Date
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" sx={{ color: 'white', mt: 0.5 }}>
                        {selectedDate ? new Date(selectedDate).toLocaleDateString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        }) : 'N/A'}
                      </Typography>
                    </Box>

                    {/* Attendance Rate - Large Display */}
                    <Box
                      sx={{
                        p: 3,
                        bgcolor: 'rgba(255, 255, 255, 0.15)',
                        borderRadius: '16px',
                        backdropFilter: 'blur(10px)',
                        textAlign: 'center',
                      }}
                    >
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', textTransform: 'uppercase', letterSpacing: 1 }}>
                        Attendance Rate
                      </Typography>
                      <Typography variant="h2" fontWeight="bold" sx={{ color: 'white', my: 1 }}>
                        {overview.total_employees > 0
                          ? ((overview.present_count / overview.total_employees) * 100).toFixed(1)
                          : 0}%
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        {overview.present_count} of {overview.total_employees} present
                      </Typography>
                    </Box>

                    {/* Total Daily Cost */}
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: 'rgba(255, 255, 255, 0.15)',
                        borderRadius: '16px',
                        backdropFilter: 'blur(10px)',
                      }}
                    >
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', textTransform: 'uppercase', letterSpacing: 1 }}>
                        Total Daily Cost
                      </Typography>
                      <Typography variant="h5" fontWeight="bold" sx={{ color: 'white', mt: 0.5 }}>
                        {formatCurrency(overview.total_daily_cost)}
                      </Typography>
                    </Box>

                    {/* Breakdown - Grid Layout */}
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: 'rgba(255, 255, 255, 0.15)',
                        borderRadius: '16px',
                        backdropFilter: 'blur(10px)',
                      }}
                    >
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', textTransform: 'uppercase', letterSpacing: 1, mb: 2, display: 'block' }}>
                        Breakdown
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mt: 2 }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: '12px',
                              bgcolor: 'rgba(76, 175, 80, 0.3)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mx: 'auto',
                              mb: 1,
                            }}
                          >
                            <CheckCircle sx={{ color: '#4caf50', fontSize: 24 }} />
                          </Box>
                          <Typography variant="h6" fontWeight="bold" sx={{ color: 'white' }}>
                            {overview.present_count}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                            Present
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: '12px',
                              bgcolor: 'rgba(244, 67, 54, 0.3)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mx: 'auto',
                              mb: 1,
                            }}
                          >
                            <Cancel sx={{ color: '#f44336', fontSize: 24 }} />
                          </Box>
                          <Typography variant="h6" fontWeight="bold" sx={{ color: 'white' }}>
                            {overview.absent_count}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                            Absent
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: '12px',
                              bgcolor: 'rgba(33, 150, 243, 0.3)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mx: 'auto',
                              mb: 1,
                            }}
                          >
                            <Schedule sx={{ color: '#2196f3', fontSize: 24 }} />
                          </Box>
                          <Typography variant="h6" fontWeight="bold" sx={{ color: 'white' }}>
                            {overview.half_day_count}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                            Half-Day
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  )
}

export { AttendanceManagementPageFinal }
export default AttendanceManagementPageFinal
